import React, { useCallback, useEffect, useRef, useState } from 'react';
import { uiZoom } from '../../utils/uiScale';

const OUTPUT_SIZE = 512;
const MAX_ZOOM = 5;

const loadBitmap = async (file) => {
    if ('createImageBitmap' in window) {
        try { return await createImageBitmap(file, { imageOrientation: 'from-image' }); } catch {}
    }
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('사진을 읽을 수 없습니다. (HEIC 등은 JPG로 바꿔서 올려 주세요)')); };
        img.src = url;
    });
};

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

/**
 * 프로필 사진 자르기: 끌어서 위치 이동, 슬라이더·휠·두 손가락으로 확대/축소.
 * 동그라미 안이 실제로 보이는 영역이다.
 */
export function AvatarCropper({ file, onCancel, onConfirm, busy }) {
    const canvasRef = useRef(null);
    const boxRef = useRef(null);
    const [image, setImage] = useState(null);
    const [error, setError] = useState('');
    // view: 원본 사진 좌표계에서 잘라낼 정사각형의 중심(cx, cy)과 확대 배율(zoom, 1 = 짧은 변에 꽉 차게)
    const [view, setView] = useState({ cx: 0, cy: 0, zoom: 1 });
    const pointers = useRef(new Map());
    const gesture = useRef(null);

    useEffect(() => {
        let alive = true;
        loadBitmap(file).then(img => {
            if (!alive) return;
            const w = img.width || img.naturalWidth, h = img.height || img.naturalHeight;
            setImage({ img, w, h });
            setView({ cx: w / 2, cy: h / 2, zoom: 1 });
        }).catch(err => alive && setError(err.message));
        return () => { alive = false; };
    }, [file]);

    // 잘라낼 영역이 사진 밖으로 나가지 않게 맞춘다
    const normalize = useCallback((next) => {
        if (!image) return next;
        const zoom = clamp(next.zoom, 1, MAX_ZOOM);
        const side = Math.min(image.w, image.h) / zoom;
        return {
            zoom,
            cx: clamp(next.cx, side / 2, image.w - side / 2),
            cy: clamp(next.cy, side / 2, image.h - side / 2),
        };
    }, [image]);

    const sourceRect = useCallback((v) => {
        const side = Math.min(image.w, image.h) / v.zoom;
        return [v.cx - side / 2, v.cy - side / 2, side, side];
    }, [image]);

    // 미리보기 그리기
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !image) return;
        const size = canvas.clientWidth;
        const dpr = window.devicePixelRatio || 1;
        if (canvas.width !== Math.round(size * dpr)) { canvas.width = Math.round(size * dpr); canvas.height = Math.round(size * dpr); }
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingQuality = 'high';
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image.img, ...sourceRect(view), 0, 0, canvas.width, canvas.height);
    }, [image, view, sourceRect]);

    const screenToSource = () => (image ? Math.min(image.w, image.h) / view.zoom / (boxRef.current?.clientWidth || 1) / uiZoom() : 1);

    const onPointerDown = (e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        gesture.current = null;
    };
    const onPointerMove = (e) => {
        const prev = pointers.current.get(e.pointerId);
        if (!prev || !image) return;
        const pts = pointers.current;
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pts.size === 1) {
            const ratio = screenToSource();
            setView(v => normalize({ ...v, cx: v.cx - (e.clientX - prev.x) * ratio, cy: v.cy - (e.clientY - prev.y) * ratio }));
        } else if (pts.size === 2) {
            const [a, b] = [...pts.values()];
            const dist = Math.hypot(a.x - b.x, a.y - b.y);
            if (!gesture.current) gesture.current = { dist, zoom: view.zoom };
            else setView(v => normalize({ ...v, zoom: gesture.current.zoom * (dist / gesture.current.dist) }));
        }
    };
    const onPointerUp = (e) => { pointers.current.delete(e.pointerId); if (pointers.current.size < 2) gesture.current = null; };

    useEffect(() => {
        const box = boxRef.current;
        if (!box) return undefined;
        const onWheel = (e) => { e.preventDefault(); setView(v => normalize({ ...v, zoom: v.zoom * Math.exp(-e.deltaY * 0.0015) })); };
        box.addEventListener('wheel', onWheel, { passive: false });
        return () => box.removeEventListener('wheel', onWheel);
    }, [normalize, image]);

    const confirm = async () => {
        const out = document.createElement('canvas');
        const side = Math.min(image.w, image.h) / view.zoom;
        out.width = out.height = Math.round(Math.min(OUTPUT_SIZE, side));
        const ctx = out.getContext('2d');
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(image.img, ...sourceRect(view), 0, 0, out.width, out.height);
        const toBlob = (type, q) => new Promise(r => out.toBlob(r, type, q));
        const webp = await toBlob('image/webp', 0.9);
        onConfirm(webp?.type === 'image/webp' ? webp : await toBlob('image/jpeg', 0.9));
    };

    if (error) return (
        <div className="cropper">
            <p className="profile-hint error">{error}</p>
            <div className="profile-actions"><button className="profile-btn" onClick={onCancel}>돌아가기</button></div>
        </div>
    );

    return (
        <div className="cropper">
            <div ref={boxRef} className="cropper-box" onPointerDown={onPointerDown} onPointerMove={onPointerMove}
                onPointerUp={onPointerUp} onPointerCancel={onPointerUp} aria-label="사진 위치 조정 영역">
                <canvas ref={canvasRef} className="cropper-canvas" />
                <div className="cropper-mask" aria-hidden="true" />
                {!image && <div className="cropper-loading">불러오는 중...</div>}
            </div>
            <label className="cropper-zoom">
                <span aria-hidden="true">➖</span>
                <input type="range" min="1" max={MAX_ZOOM} step="0.01" value={view.zoom} aria-label="확대"
                    onChange={e => setView(v => normalize({ ...v, zoom: Number(e.target.value) }))} disabled={!image} />
                <span aria-hidden="true">➕</span>
            </label>
            <p className="profile-hint">끌어서 위치를 옮기고, 슬라이더·마우스 휠·두 손가락으로 확대하세요.</p>
            <div className="profile-actions">
                <button className="profile-btn primary" onClick={confirm} disabled={!image || busy}>{busy ? '저장 중...' : '이대로 적용'}</button>
                <button className="profile-btn" onClick={() => image && setView({ cx: image.w / 2, cy: image.h / 2, zoom: 1 })} disabled={!image || busy}>원래대로</button>
                <button className="profile-btn ghost" onClick={onCancel} disabled={busy}>취소</button>
            </div>
        </div>
    );
}
